import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { MessageCircle, Send, Trash2, Loader2, ShieldCheck, ThumbsUp } from "lucide-react";
import { UserBadges } from "./UserBadges";

export const CaseComments = ({ reportId }) => {
  const { user, isMod, openAuth } = useAuth();
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);

  const load = () => api.get(`/reports/${reportId}/comments`).then(({ data }) => setComments(data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { setLoading(true); load(); /* eslint-disable-next-line */ }, [reportId]);

  const post = async () => {
    if (!user) return openAuth();
    if (!text.trim()) return;
    setPosting(true);
    try {
      const { data } = await api.post(`/reports/${reportId}/comments`, { text });
      setComments((c) => [...c, data]);
      setText("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not post comment.");
    } finally {
      setPosting(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/comments/${id}`);
      setComments((c) => c.filter((x) => x.id !== id));
    } catch { toast.error("Could not delete comment."); }
  };

  const like = async (id) => {
    if (!user) return openAuth();
    try {
      const { data } = await api.post(`/comments/${id}/like`);
      setComments((cs) => cs.map((x) => (x.id === id ? { ...x, likes: data.likes, i_liked: data.i_liked } : x)));
    } catch { /* ignore */ }
  };

  return (
    <div className="mt-6 border-t border-[#1E293F] pt-5" data-testid="case-comments">
      <h4 className="flex items-center gap-2 text-sm font-bold text-white">
        <MessageCircle size={16} className="text-[#00F0FF]" /> Discussion
        <span className="text-[#64748B]">({comments.length})</span>
      </h4>

      <div className="mt-3 space-y-3">
        {loading ? (
          <div className="flex justify-center py-4 text-[#64748B]"><Loader2 className="animate-spin" size={18} /></div>
        ) : comments.length === 0 ? (
          <p className="text-sm text-[#64748B]" data-testid="comments-empty">No comments yet. Faced this player? Share what you saw.</p>
        ) : comments.map((c) => (
          <div key={c.id} data-testid={`comment-${c.id}`} className="rounded-lg border border-[#1E293F] bg-[#141B2D] p-3">
            <div className="flex items-center gap-2 text-xs">
              <span className="font-semibold text-white">{c.user_name}</span>
              {c.is_mod && <span className="inline-flex items-center gap-0.5 rounded bg-[#00F0FF]/15 px-1.5 py-0.5 font-bold text-[#00F0FF]"><ShieldCheck size={10} /> MOD</span>}
              <UserBadges supporter={c.is_supporter} tipper={c.is_tipper} />
              <span className="text-[#64748B]">{new Date(c.created_at).toLocaleDateString()}</span>
              {(isMod || c.user_id === user?.user_id) && (
                <button onClick={() => remove(c.id)} data-testid={`comment-delete-${c.id}`} className="ml-auto text-[#64748B] hover:text-[#FF2A55]"><Trash2 size={13} /></button>
              )}
            </div>
            <p className="mt-1.5 whitespace-pre-wrap break-words text-sm text-[#CBD5E1]">{c.text}</p>
            <button onClick={() => like(c.id)} data-testid={`comment-like-${c.id}`}
              className={`mt-2 inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-semibold transition-colors ${c.i_liked ? "bg-[#00F0FF]/15 text-[#00F0FF]" : "text-[#64748B] hover:text-[#00F0FF]"}`}>
              <ThumbsUp size={12} /> {c.likes || 0}
            </button>
          </div>
        ))}
      </div>

      <div className="mt-3 flex gap-2">
        <textarea
          data-testid="comment-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onFocus={() => !user && openAuth()}
          rows={2}
          maxLength={800}
          placeholder={user ? "Add a comment…" : "Sign in to comment"}
          className="flex-1 resize-none rounded-lg border border-[#1E293F] bg-[#141B2D] px-3 py-2 text-sm text-white placeholder:text-[#64748B] focus:border-[#00F0FF]/40 focus:outline-none"
        />
        <Button data-testid="comment-post-btn" onClick={post} disabled={posting || !text.trim()} className="self-end bg-[#00F0FF] font-bold text-[#090C15] hover:bg-[#00F0FF]/90">
          {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send size={15} />}
        </Button>
      </div>
    </div>
  );
};
